import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

/**
 * The ERROR state for a failed query. The rule it enforces: a query that failed
 * must never fall through to the empty state. "No stores connected yet" and
 * "we couldn't reach the server" are different facts, and showing the first
 * when the second is true tells the user something false about their account.
 *
 * Copy shape is cause-then-remedy: a plain-language sentence saying what
 * couldn't be loaded, the raw `error.message` as supporting detail, and a
 * Retry wired to the query's own `refetch`.
 */
export interface QueryErrorProps {
  /** Plain-language, user-facing: "Couldn't load your stores." */
  message: string;
  /** The underlying error text, shown as secondary detail. Never the whole message. */
  detail?: string;
  onRetry?: () => void;
  /** Whether the retry is in flight. */
  retrying?: boolean;
}

export function QueryError({ message, detail, onRetry, retrying }: QueryErrorProps) {
  return (
    <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
      <Stack gap="xs">
        <Text size="sm">{message}</Text>
        {detail && (
          <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
            {detail}
          </Text>
        )}
        {onRetry && (
          <Group>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconRefresh size={14} />}
              loading={retrying}
              onClick={onRetry}
            >
              Retry
            </Button>
          </Group>
        )}
      </Stack>
    </Alert>
  );
}
